"use client";

import { cn } from "@/lib/utils";
import React, { useRef } from "react";
import { ButtonStyleType, SpacingType } from "@/types";
import { sectionVariants } from "@/types";
import { Button } from "@/components/ui/button";
import Image from '@/components/ui/Image';
import { useEffect, useState } from "react";
import parse from "html-react-parser";
import Link from "next/link";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { SectionWrapper } from "../components/SectionWraper";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface GalleryItem {
  image?: string;
  imageAltText?: string;
  title?: string;
  content?: string;
  link?: string;
  tabTitle?: string;
  icon?: string;
}

interface Gallery5Props { 
  darkMode?: boolean; 
  variant?: sectionVariants;
  spacing?: SpacingType;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  themeChangeOnScroll?: boolean;
  title?: string;
  content?: string;
  hasButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonStyle?: ButtonStyleType;
  microCopy?: string;
  galleryItems?: GalleryItem[];
  attributes?: React.HTMLProps<HTMLElement>;
}

const Gallery5 = ({
  darkMode = false,
  variant = "A",
  spacing = "xlarge",
  usedInPageNav = false,
  pageNavTitle = "Gallery 5",
  themeChangeOnScroll = false,
  title,
  content,
  hasButton,
  buttonText,
  buttonLink,
  buttonStyle,
  microCopy,
  galleryItems,
  attributes,
}: Gallery5Props) => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(galleryItems?.[0]?.tabTitle);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]); 

  useEffect(() => {
    if (!api) {
      return;
    }

    const updateSelection = () => {
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    };
    updateSelection();
    api.on("scroll", updateSelection);

    const currentIndex = galleryItems?.findIndex((item) => item.tabTitle === current);
    api.scrollTo(currentIndex ?? 0);

    const onSelect = () => {
      const idx = api.selectedScrollSnap();
      setCurrent(galleryItems?.[idx]?.tabTitle);
    };
    api.on("select", onSelect);

    return () => {
      api.off("select", onSelect);
    };
  }, [api, current, galleryItems]);

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="gallery5"
      attributes={attributes}
      className="overflow-hidden"
    >   
      <div className="container" data-aos="fade-up">
        <div className="mb-0 md:mb-16 flex flex-col text-center">
          {title && (
            <h2 className="max-w-[500px] lg:max-w-[800px] mx-auto">
              {parse(title)}
            </h2>
          )}
          {content && (
            <div className="mt-6 paragraph-lg text-muted-foreground max-w-2xl mx-auto">
              {parse(content)}
            </div>
          )}
          {hasButton && (
            <div>
              <Button asChild variant={buttonStyle} size="lg" className="mt-10 w-full sm:w-auto">
                <Link href={buttonLink ?? ""}>
                  {buttonText}
                </Link>
              </Button>
              {microCopy && (
                <div className="mt-2 paragraph-sm text-muted-foreground text-center">
                  {microCopy}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="relative container" data-aos="fade-up" data-aos-delay={100}>
        <Carousel
          setApi={setApi}
          className="[&>div[data-slot=carousel-content]]:overflow-visible"
        >
          <div className="items-center justify-between mb-8 md:mb-20 lg:mb-40 hidden md:flex">
            <Tabs
              value={current}
              onValueChange={setCurrent}
              className="justify-center flex m-auto"
            >
              <TabsList className="relative h-16 p-1 gap-0 mx-auto rounded-full">
                {galleryItems?.map((item, idx) => (
                  <TabsTrigger
                    key={idx}
                    ref={(el) => {
                      tabRefs.current[idx] = el;
                    }}
                    value={item.tabTitle ?? ""}
                    className="paragraph-base px-7 transition-all duration-700 ease-out [&[data-state=active]]:shadow-none [&[data-state=active]]:font-bold rounded-full cursor-pointer"
                  >
                    {item.tabTitle}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <div className="items-center gap-4 hidden">
              <div className="mt-6 flex shrink-0 items-center justify-end gap-2 sm:mt-0">
                <Button
                  size="iconSm"
                  variant="outline"
                  grayBorder
                  onClick={() => {
                    api?.scrollPrev();
                  }}
                  disabled={!canScrollPrev}
                  className="disabled:pointer-events-auto"
                >
                  <span className="material-symbols-outlined">
                    arrow_back  
                  </span>
                </Button>
                <Button
                  size="iconSm"
                  variant="outline"
                  grayBorder
                  onClick={() => {
                    api?.scrollNext();
                  }}
                  disabled={!canScrollNext}
                  className="disabled:pointer-events-auto"
                >
                  <span className="material-symbols-outlined">
                    arrow_forward
                  </span>
                </Button>
              </div>
            </div>
          </div>
          <CarouselContent className="max-w-[92%] md:max-w-5xl">
            {galleryItems?.map((item, idx) => (
              <CarouselItem key={idx} className="w-fit max-w-5xl pl-4 md:pl-6">
                {item.link ? (
                  <Link href={item.link ?? ""} tabIndex={-1}>
                    <div className={cn("grid h-full max-w-5xl md:max-h-[512px] md:grid-cols-2")}>
                      <div className="flex flex-col py-8 sm:pr-6 lg:p-10 md:col-span-1 lg:order-2 lg:items-center lg:justify-center">
                        <div className="flex flex-col">
                          {item.icon && (
                            <span className="material-symbols-outlined text-brand">
                              {item.icon}
                            </span>
                          )}
                          {item.title && (
                            <h3 className="mt-6">
                              {parse(item.title ?? "")}
                            </h3>
                          )}
                          {item.content && (
                            <div className="paragraph-base text-muted-foreground mt-4 lg:mt-8">
                              {parse(item.content ?? "")}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="rounded-r-xl md:col-span-1 lg:order-1">
                        <Image
                          src={item.image ?? ""}
                          alt={item.imageAltText ?? ""}
                          title={item.imageAltText ?? ""}
                          width={512}
                          height={512}
                          className="h-full w-full rounded-2xl object-cover"
                        />
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div className={cn("grid h-full max-w-5xl md:max-h-[512px] md:grid-cols-2")}>
                    <div className="flex flex-col py-8 sm:pr-6 lg:p-10 md:col-span-1 lg:order-2 lg:items-center lg:justify-center">
                      <div className="flex flex-col">
                        {item.icon && (
                          <span className="material-symbols-outlined text-brand">
                            {item.icon}
                          </span>
                        )}
                        {item.title && (
                          <h3 className="mt-6">
                            {parse(item.title ?? "")}
                          </h3>
                        )}
                        {item.content && (
                          <div className="paragraph-base text-muted-foreground mt-4 lg:mt-8">
                            {parse(item.content ?? "")}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="rounded-r-xl md:col-span-1 lg:order-1">
                      <Image
                        src={item.image ?? ""}
                        alt={item.imageAltText ?? ""}
                        title={item.imageAltText ?? ""}
                        width={512}
                        height={512}
                        className="h-full w-full rounded-2xl object-cover"
                      />
                    </div>
                  </div>
                )}
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>
    </SectionWrapper>
  );
};

export { Gallery5 };
